import { BrokenInAppBrowser } from "./detect";

/**
 * A working hand-off takes ~1.2s to show up as a hidden webview (measured, iOS
 * 26.5.2). Firing sooner would credit the wrong scheme in telemetry.
 */
export const ESCAPE_FIRST_ATTEMPT_INTERVAL_MS = 2_000;

/** Shorter for the long shots, so the spinner does not drag. */
export const ESCAPE_ATTEMPT_INTERVAL_MS = 1_000;

/** Grace after the last scheme before declaring the chain failed. */
export const ESCAPE_FINAL_SETTLE_MS = 1_500;

/**
 * Shared with the server-rendered escape page, which bakes these numbers into
 * its inline script, so the two cannot drift apart.
 */
export function escapeAttemptDelayMs(index: number, total: number): number {
  if (index === total - 1) return ESCAPE_FINAL_SETTLE_MS;
  if (index === 0) return ESCAPE_FIRST_ATTEMPT_INTERVAL_MS;
  return ESCAPE_ATTEMPT_INTERVAL_MS;
}

/** Also the telemetry endpoint's allowlist, so no unknown value is stored. */
export const ESCAPE_SCHEMES = [
  "itms-apps",
  "extbrowser",
  "x-safari",
  "x-safari-window",
  "googlechromes",
] as const;

export type EscapeScheme = (typeof ESCAPE_SCHEMES)[number];

export type EscapeAttempt = {
  via: "location" | "window";
  url: string;
  /** Telemetry label rather than the URL, which would put link data in analytics. */
  scheme: EscapeScheme;
  /** window.open is popup-blocked without a tap, so the automatic pass skips these. */
  needsGesture?: boolean;
};

const APP_STORE_URL_PATTERN = /^https:\/\/apps\.apple\.com\//i;
const HTTPS_URL_PATTERN = /^https:\/\//i;

/**
 * The gate on the whole workaround. Only App Store URLs break, so escaping
 * anything else would throw a visitor out of the host app for nothing.
 */
export function isBlockedDestination(destination: string): boolean {
  return APP_STORE_URL_PATTERN.test(destination);
}

/**
 * The schemes to try, in order. All undocumented and all patchable by the
 * vendor, so every caller has to stay usable when the whole list fails.
 *
 * `extbrowser` leads because it is the only one measured to work. `itms-apps`
 * stayed inert through a six-second window, so Meta blocks the App Store as a
 * destination rather than the apps.apple.com host, but it is kept for the day
 * Meta patches their own scheme.
 *
 * Kept free of imports because /api/link/continue serialises the result into an
 * inline script.
 */
export function buildEscapeAttempts(
  browser: BrokenInAppBrowser,
  destination: string
): EscapeAttempt[] {
  const attempts: EscapeAttempt[] = [];

  if (browser === "facebook") {
    // Facebook's webview blocks the extbrowser scheme but lets x-safari out,
    // and window.open is the form that gets through where assignment to
    // location does not.
    attempts.push({
      via: "window",
      url: `x-safari-${destination}`,
      scheme: "x-safari-window",
      needsGesture: true,
    });
    attempts.push({
      via: "location",
      url: `x-safari-${destination}`,
      scheme: "x-safari",
    });
  } else {
    // Instagram and Threads hand this to the external browser, which then
    // bounces into the App Store on its own.
    attempts.push({
      via: "location",
      url: `instagram://extbrowser/?url=${encodeURIComponent(destination)}`,
      scheme: "extbrowser",
    });
  }

  // Guarded because handing the App Store app a web URL just dead-ends it.
  if (isBlockedDestination(destination)) {
    attempts.push({
      via: "location",
      url: destination.replace(/^https:/i, "itms-apps:"),
      scheme: "itms-apps",
    });
  }

  // Facebook already had its x-safari pass above.
  if (browser !== "facebook") {
    attempts.push({
      via: "location",
      url: `x-safari-${destination}`,
      scheme: "x-safari",
    });
  }

  // Last resort: Chrome, if it is installed.
  if (HTTPS_URL_PATTERN.test(destination)) {
    attempts.push({
      via: "location",
      url: destination.replace(HTTPS_URL_PATTERN, "googlechromes://"),
      scheme: "googlechromes",
    });
  }

  return attempts;
}
